import {
  WSActions,
  WSAlert,
  WSCard,
  WSInfoBox,
  WSList,
  WSProgressBar
} from "@wingspanhq/fe-component-library";
import { useCallback, useEffect, useState } from "react";

import { StepComponent } from "../../../../shared/components/ModalFlow/types";
import { ErrorAlert } from "../../../../v3/components/ErrorAlert";
import { queriesInsuranceImports } from "../../../../v3/query/queriesInsuranceImports";
import { selectorInsuranceCoverageIssues } from "../../selectors/selectorInsuranceCoverageIssues";
import { selectorInsuranceImportCoverages } from "../../selectors/selectorInsuranceImportCoverages";

import { FlowUploadDocumentProps } from "./types";

export const Step3: StepComponent<FlowUploadDocumentProps> = ({
  state,
  setState,
  onClose,
  onBack,
  goToSideStep
}) => {
  const { insuranceImportId } = state;

  const [submitInsurance, submitInsuranceMeta] =
    queriesInsuranceImports.submitInsuranceImport.useMutation();

  const queryInsuranceImport =
    queriesInsuranceImports.getInsuranceImport.useQuery(
      { importId: insuranceImportId! },
      { enabled: !!insuranceImportId }
    );

  const coverages = queryInsuranceImport.data
    ? selectorInsuranceImportCoverages(queryInsuranceImport.data)
    : [];
  const importableCoverages = coverages.filter(
    coverage => selectorInsuranceCoverageIssues(coverage).length === 0
  );

  const all = coverages.length;
  const success = importableCoverages.length;

  const [retry, setRetry] = useState(0);
  const [progress, setProgress] = useState(10);

  const submit = useCallback(async () => {
    submitInsuranceMeta.reset();
    await submitInsurance(
      {
        importId: insuranceImportId!
      },
      {
        onSuccess() {
          goToSideStep("success");
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceImportId]);

  useEffect(() => {
    setProgress(10);
    submit();

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [insuranceImportId, submit, retry]);

  return (
    <WSList gap="2XL">
      {submitInsuranceMeta.isError ? (
        <>
          <WSList gap="L">
            <WSAlert icon="alert-circle" theme="error" title="Import failed">
              Something went wrong with the import. Please try the import again
              or upload a new file and start over. If the issue persists, please
              contact support.
            </WSAlert>
            <WSCard
              header={{
                label: "Insurance policies",
                value: {
                  pill: { text: "Import failed", theme: "error" }
                }
              }}
            >
              <ErrorAlert
                context="UploadFile"
                error={submitInsuranceMeta.error}
              />
            </WSCard>
          </WSList>
          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Cancel",
                name: "cancel",
                onClick: onClose,
                kind: "Tertiary",
                type: "button"
              },
              {
                name: "upload-new-file",
                label: "Upload new file",
                onClick: async () => {
                  setState({
                    fileId: undefined,
                    insuranceImportId: undefined
                  });
                  onBack();
                },
                kind: "Secondary",
                type: "button"
              },
              {
                label: "Try import again",
                kind: "Primary",
                onAsyncClick: async () => {
                  setRetry(retry => retry + 1);
                }
              }
            ]}
          />
        </>
      ) : (
        <>
          <WSCard
            header={{
              label: "Insurance policies",
              value: {
                pill: {
                  text: "Importing",
                  theme: "blue"
                }
              }
            }}
          >
            <WSProgressBar
              my="L"
              percent={progress}
              text={
                <WSInfoBox icon="time-five" mt="S" theme="info" transparent>
                  {success} of {all} policies imported. If you close this page,
                  the progress will continue until it is completed.
                </WSInfoBox>
              }
              theme="neutral"
            />
          </WSCard>
          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Continue",
                disabled: true
              }
            ]}
          />
        </>
      )}
    </WSList>
  );
};
